/**
 * Platform owner/super-admin console calls (Task #25). Every request carries the signed-in
 * platform admin's token; the server re-checks platform-admin + aal2 on each one (in the shared
 * requireAdmin gate — see server/src/rest/tenants/admin.ts), and the database enforces suspension.
 * The browser is never trusted to decide admin rights.
 */
import axios from 'axios';
import { apiBaseUrl } from './apiBase.ts';
import type { AxiosError } from 'axios';
import { getAccessToken } from './authClient.ts';

/** Every partner firm + operational facts (no client data) — see server/src/rest/tenants/admin.ts's adminTenantSchema. */
export interface AdminTenant {
  id: string;
  name: string;
  status: string;
  profession: string | null;
  afslLicenseeName: string | null;
  afslNumber: string | null;
  createdAt: string;
  memberCount: number;
  ownerEmail: string;
  plan: string | null;
  hasAccess: boolean;
  freeAccess: boolean;
}

/** An entry on the owner's free-access allowlist. */
export interface FreeAccessEmail {
  email: string;
  note: string | null;
  createdAt: string;
}

interface AdminErrorBody {
  error?: { code?: string; message?: string };
}

export function getApiBaseUrl(): string {
  // One source for this — see services/apiBase.ts for why eight copies was a problem.
  return apiBaseUrl();
}

async function authHeaders(): Promise<{ Authorization: string; 'Content-Type': string }> {
  const token = await getAccessToken();
  if (!token) throw new Error('Not signed in');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export function explainAdminError(err: AxiosError<AdminErrorBody> | Error | unknown, fallback: string): string {
  const axiosErr = err as AxiosError<AdminErrorBody> | undefined;
  const code = axiosErr?.response?.data?.error?.code;
  if (code === 'MFA_REQUIRED') {
    /*
     * Two different situations, one message, because the browser cannot tell them apart from a 403:
     * they have no authenticator yet, or they have one and this session was only ever a password (a
     * password reset ends up here, deliberately). Both are fixed from the same two places, and there
     * is no mid-session step-up screen yet — so the honest instruction is to sign in again.
     */
    return "The admin console needs a code from your authenticator. If you haven't set one up yet, do that under Account; otherwise sign out and back in, and enter a code.";
  }
  const serverMsg = axiosErr?.response?.data?.error?.message;
  if (serverMsg) return serverMsg;
  if ((err as Error)?.message === 'Not signed in') return 'Your session has expired — please sign in again.';
  if (!axiosErr?.response) return "We couldn't reach the server. Check your connection and try again.";
  return fallback;
}

/**
 * Is the signed-in user a platform admin? Drives whether the Admin entry point is shown at all.
 *
 * WHY THIS RETRIES. The answer is fetched once per sign-in and held in React state for the rest of
 * the session, so a single failed call does not degrade anything — it removes the Admin button
 * entirely, for the whole session, with nothing on screen to say so. And the one moment it is called
 * is the worst one: right after signing in or finishing the partner wizard, when the access token may
 * be mid-rotation (enrolling in two-factor deliberately replaces the session).
 *
 * "I could not tell" and "no, they are not an admin" are the same value to the caller, so the only
 * defence is not to accept the first no. One retry after a short pause turns a blip into a delay
 * instead of a missing feature. A genuine non-admin costs one extra request, once.
 */
export async function getIsPlatformAdmin(): Promise<boolean> {
  return (await getAdminAccess()).isAdmin;
}

/** What this signed-in user may open: the admin console, and/or the licensee oversight view. */
export interface AdminAccess {
  isAdmin: boolean;
  /** May review advice given under our licence (the database decides; see migration 0030). */
  canReviewAdvice: boolean;
}

export async function getAdminAccess(): Promise<AdminAccess> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const headers = await authHeaders();
      const res = await axios.get(`${getApiBaseUrl()}/rest/admin/whoami`, { headers, timeout: 30_000 });
      return { isAdmin: Boolean(res.data?.isAdmin), canReviewAdvice: Boolean(res.data?.canReviewAdvice) };
    } catch {
      // Only the first failure is worth waiting on; the second is an answer.
      if (attempt === 0) await new Promise((resolve) => setTimeout(resolve, 1_500));
    }
  }
  return { isAdmin: false, canReviewAdvice: false };
}

/** Platform-wide usage: counts and dates only (server/src/shared/db/migrations/0030). */
export interface PlatformUsage {
  generatedAt: string;
  firms: { total: number; active: number; notActive: number; ownLicence: number; underOurLicence: number };
  advisers: number;
  clientFiles: { total: number; viaFirms: number; direct: number; factFindComplete: number; new30d: number; new90d: number; updated30d: number };
  soas: { total: number; published: number; ourLicence: number; firmLicence: number; new30d: number };
  soasByMonth: { month: string; generated: number; published: number; ourLicence: number }[];
  savedScenariosByModule: Record<string, number>;
  accounts: { total: number; verified: number; new30d: number; active30d: number; active90d: number };
  byFirm: {
    id: string;
    name: string;
    profession: string | null;
    status: string;
    advisers: number;
    clientFiles: number;
    factFindsComplete: number;
    soasGenerated: number;
    soasPublished: number;
    lastActivity: string | null;
  }[];
}

export async function getPlatformUsage(): Promise<PlatformUsage> {
  const headers = await authHeaders();
  const res = await axios.get(`${getApiBaseUrl()}/rest/admin/usage`, { headers, timeout: 30_000 });
  return res.data.usage as PlatformUsage;
}

/**
 * De-identified client insights for clients advised under our licence (migration 0031). Every count
 * under `minGroup` arrives as null — "too few to show" — so no band can be traced to a person.
 */
export interface ClientInsightsReport {
  generatedAt: string;
  minGroup: number;
  filesCounted: number;
  tooFew: boolean;
  goals?: Record<string, number | null>;
  household?: Record<string, number | null>;
  homeOwner?: Record<string, number | null>;
  ageBand?: Record<string, number | null>;
  homeValueBand?: Record<string, number | null>;
  superBand?: Record<string, number | null>;
  netWealthBand?: Record<string, number | null>;
  livingExpensesBand?: Record<string, number | null>;
  goalsByNetWealth?: { band: string; goal: string; count: number | null }[];
}

export async function getClientInsights(): Promise<ClientInsightsReport> {
  const headers = await authHeaders();
  const res = await axios.get(`${getApiBaseUrl()}/rest/admin/insights`, { headers, timeout: 30_000 });
  return res.data.insights as ClientInsightsReport;
}

/** One Statement of Advice given under our licence, as the oversight register lists it. */
export interface AdviceRegisterEntry {
  soaId: string;
  profileId: string;
  clientName: string;
  partnerName: string | null;
  channel: 'direct' | 'firm';
  firmName: string | null;
  module: string;
  version: number;
  status: 'draft' | 'published';
  createdAt: string;
  publishedAt: string | null;
  keepUntil: string;
  fileDeleted: boolean;
}

export interface OpenedAdviceRecord {
  soaId: string;
  module: string;
  version: number;
  status: string;
  licenseeName: string | null;
  afslNumber: string | null;
  createdAt: string;
  publishedAt: string | null;
  keepUntil: string;
  basisSealedAt: string;
  document: unknown;
  basis: { identity: Record<string, unknown>; factFind: unknown };
}

/** Every SOA given under our licence. The server logs each call on the audit trail. */
export async function listAdviceRegister(): Promise<AdviceRegisterEntry[]> {
  const headers = await authHeaders();
  const res = await axios.get(`${getApiBaseUrl()}/rest/compliance/advice`, { headers, timeout: 30_000 });
  return (res.data?.records ?? []) as AdviceRegisterEntry[];
}

/** Open one advice record. The reason is written to the audit trail before anything is returned. */
export async function openAdviceRecord(soaId: string, reason: string): Promise<OpenedAdviceRecord> {
  const headers = await authHeaders();
  const res = await axios.post(`${getApiBaseUrl()}/rest/compliance/advice/${soaId}/open`, { reason }, { headers, timeout: 30_000 });
  return res.data.record as OpenedAdviceRecord;
}

/** Every partner firm + operational facts (no client data). */
export async function listAdminTenants(): Promise<AdminTenant[]> {
  const headers = await authHeaders();
  const res = await axios.get(`${getApiBaseUrl()}/rest/admin/tenants`, { headers, timeout: 30_000 });
  return res.data?.tenants || [];
}

/** Approve (activate) or suspend a firm. status: 'active' | 'suspended'. */
export async function setTenantStatus(tenantId: string, status: 'active' | 'suspended'): Promise<{ ok: boolean; status: string }> {
  const headers = await authHeaders();
  const res = await axios.post(
    `${getApiBaseUrl()}/rest/admin/tenants/${tenantId}/status`,
    { status },
    { headers, timeout: 30_000 },
  );
  return res.data;
}

/** Grant a firm complimentary "Free access" (revocable). */
export async function grantFreeAccess(tenantId: string): Promise<{ ok: boolean }> {
  const headers = await authHeaders();
  const res = await axios.post(`${getApiBaseUrl()}/rest/admin/tenants/${tenantId}/free-access`, {}, { headers, timeout: 30_000 });
  return res.data;
}

/** Remove free access from a firm (locks them out; never touches a paid subscription). */
export async function removeFreeAccess(tenantId: string): Promise<{ ok: boolean }> {
  const headers = await authHeaders();
  const res = await axios.delete(`${getApiBaseUrl()}/rest/admin/tenants/${tenantId}/free-access`, { headers, timeout: 30_000 });
  return res.data;
}

/**
 * Pre-authorise an email for free access (owner allowlist). Works before OR after the person signs
 * up — they're never charged when they enter. Returns { applied, tenantName }.
 */
export async function preauthoriseFreeAccessByEmail(email: string): Promise<{ ok: boolean; applied: boolean; tenantName: string | null }> {
  const headers = await authHeaders();
  const res = await axios.post(`${getApiBaseUrl()}/rest/admin/free-access/by-email`, { email }, { headers, timeout: 30_000 });
  return res.data;
}

/** The owner's free-access allowlist: [{ email, note, createdAt }]. */
export async function listFreeAccessEmails(): Promise<FreeAccessEmail[]> {
  const headers = await authHeaders();
  const res = await axios.get(`${getApiBaseUrl()}/rest/admin/free-access/emails`, { headers, timeout: 30_000 });
  return res.data?.emails || [];
}

/** Remove an email from the allowlist (revokes any live free access for an existing account). */
export async function removeFreeAccessByEmail(email: string): Promise<{ ok: boolean }> {
  const headers = await authHeaders();
  const res = await axios.delete(`${getApiBaseUrl()}/rest/admin/free-access/by-email`, { headers, data: { email }, timeout: 30_000 });
  return res.data;
}

/**
 * Regulatory Prompts (Platform Admin): government-source changes the weekly sweep judged relevant to the
 * engine. See server/src/shared/regulatory/. No client data.
 */
export interface RegulatoryPrompt {
  id: string;
  source: string;
  sourceLabel: string;
  title: string;
  url: string;
  publishedAt: string | null;
  detectedAt: string;
  relevant: boolean;
  confidence: 'high' | 'medium' | 'low' | null;
  summary: string | null;
  affectedModules: string[];
  sourceUrls: string[];
  status: 'new' | 'reviewed' | 'dismissed' | 'actioned';
  reviewedAt: string | null;
}

/** The last sweep — the heartbeat, so silence is never mistaken for "no changes". */
export interface RegulatoryRun {
  startedAt: string;
  finishedAt: string | null;
  ok: boolean | null;
  trigger: string;
  feedsChecked: number;
  itemsSeen: number;
  itemsNew: number;
  itemsRelevant: number;
  error: string | null;
}

export type RegulatoryStatus = RegulatoryPrompt['status'];

/** The prompts + the last sweep. `includeDismissed` also returns the items the AI judged NOT relevant. */
export async function getRegulatoryPrompts(includeDismissed = false): Promise<{ prompts: RegulatoryPrompt[]; lastRun: RegulatoryRun | null }> {
  const headers = await authHeaders();
  const res = await axios.get(`${getApiBaseUrl()}/rest/admin/regulatory-prompts`, {
    headers,
    params: { includeDismissed },
    timeout: 30_000,
  });
  return { prompts: (res.data?.prompts ?? []) as RegulatoryPrompt[], lastRun: (res.data?.lastRun ?? null) as RegulatoryRun | null };
}

/** Move a prompt through the workflow: new | reviewed | dismissed | actioned. */
export async function setRegulatoryStatus(id: string, status: RegulatoryStatus): Promise<{ ok: boolean }> {
  const headers = await authHeaders();
  const res = await axios.post(`${getApiBaseUrl()}/rest/admin/regulatory-prompts/${id}/status`, { status }, { headers, timeout: 30_000 });
  return res.data;
}

/** Run a sweep now (the weekly timer is the normal path). May take a while — fetches + triages feeds. */
export async function runRegulatorySweep(): Promise<{ ok: boolean; result: { skipped: boolean; feedsChecked: number; itemsSeen: number; itemsNew: number; itemsRelevant: number } }> {
  const headers = await authHeaders();
  const res = await axios.post(`${getApiBaseUrl()}/rest/admin/regulatory-prompts/run`, {}, { headers, timeout: 120_000 });
  return res.data;
}

/** The generated OpenAPI 3 document for the whole /rest/* API — rendered by ApiDocsConsole. */
export async function getOpenApiSpec(): Promise<Record<string, unknown>> {
  const headers = await authHeaders();
  const res = await axios.get(`${getApiBaseUrl()}/rest/admin/openapi.json`, { headers, timeout: 30_000 });
  return res.data;
}
